import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './styles/TextInput.module.css';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import { useInteractionMode } from '../contexts/InteractionMode';

/** Simple body portal for truly top-most overlay */
function BodyPortal({ children }: { children: React.ReactNode }) {
  const el = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);
  return ReactDOM.createPortal(children, el);
}

const InputField = ({
  label,
  placeholder,
  value,
  left = 0,
  top = 0,
  width = 200,
  height = 40,
  previewLabel = false,
  required = false, // default required (builder-time)
}: any) => {
  // Editor enabled flag + actions + query (used to uncheck other preview labels)
  const { enabled, actions, query } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const interactionMode = useInteractionMode();

  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const { selected } = useSelector((state: any) => state.formToggle);

  // Local UI state
  const [isDragging, setDragging] = React.useState(false);
  const [isResizing, setResizing] = React.useState(false);
  const [showEditor, setShowEditor] = React.useState(false);

  // Runtime "required" for FILL mode (doesn't mutate layout)
  const [requiredRuntime, setRequiredRuntime] =
    React.useState<boolean>(!!required);
  React.useEffect(() => {
    // if layout changes default, sync initial runtime value
    setRequiredRuntime(!!required);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required, id]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on Escape
  React.useEffect(() => {
    if (!showEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditor(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditor]);

  // Drag to move (FORM & REPORT)
  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor || isResizing) return;
    e.preventDefault();
    setDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.left = initialLeft + dx;
        props.top = initialTop + dy;
      });
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Drag corner to resize (FORM only)
  const onResizeMouseDown = (e: React.MouseEvent) => {
    if (!enabled) return;
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialWidth = width;
    const initialHeight = height;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.width = Math.max(60, initialWidth + dx);
        props.height = Math.max(30, initialHeight + dy);
      });
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Open modal editor (FORM only)
  const onDoubleClick = () => {
    if (!enabled) return;
    setShowEditor(true);
  };

  // Delete node (FORM only)
  const handleDelete = () => {
    actions.delete(id);
  };

  // Ensure only one InputField has previewLabel=true at a time
  const uncheckOtherPreviewLabels = (selfId: string) => {
    try {
      const tree = JSON.parse(query.serialize()) as Record<string, any>;
      for (const [key, node] of Object.entries(tree)) {
        if (
          key !== selfId &&
          // Craft resolves name into `type.resolvedName`; your older code checked there
          // but here we can also allow displayName on craft config
          (node?.type?.resolvedName === 'InputField' ||
            node?.displayName === 'InputField') &&
          (node?.props?.previewLabel === true ||
            node?.props?.previewLabel === 'true' ||
            node?.props?.previewLabel === 1)
        ) {
          actions.setProp(key, (p: any) => {
            p.previewLabel = false;
          });
        }
      }
    } catch {
      // ignore parse errors
    }
  };

  // Choose classes per view
  function cls() {
    switch (interactionMode) {
      case 'VIEW':
        return {
          wrapper: styles.wrapperReport,
          inputContainer: styles.inputContainerReport,
          input: styles.inputReport,
          label: styles.labelReport,
        };
      case 'BUILD':
      case 'FILL':
        return {
          wrapper: styles.wrapperForm,
          inputContainer: styles.inputContainerForm,
          input: styles.inputForm,
          label: styles.labelForm,
        };
      default:
        return { wrapper: '' } as any;
    }
  }

  const isFill = interactionMode === 'FILL';
  const isView = interactionMode === 'VIEW';
  const isForm = selected === 'FORM';

  return (
    <>
      <div
        ref={(ref) => {
          wrapperRef.current = ref;
          if (ref) connect(ref);
        }}
        className={`${cls().wrapper} ${isDragging || isResizing ? styles.isActive : ''}`}
        style={{
          left,
          top,
          position: 'absolute',
          cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        data-preview-label={previewLabel ? 'true' : 'false'}
        data-required={isFill ? String(!!requiredRuntime) : String(!!required)}
      >
        {/* Show label ONLY in FORM/FILL (not in VIEW) */}
        {!isView && (
          <label className={cls().label} title={label}>
            {label}
            {(isFill ? requiredRuntime : required) && (
              <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                *
              </span>
            )}
            {previewLabel && (
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
                ✅ Preview
              </span>
            )}
            {enabled && (
              <div
                className={styles.deleteButton}
                onClick={handleDelete}
                role="button"
                tabIndex={0}
              >
                ×
              </div>
            )}
          </label>
        )}

        {/* VIEW mode: static text */}
        {isView && (
          <div className={styles.viewContainer}>
            <p className={styles.viewLabel}>
              {label}
              {required && (
                <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                  *
                </span>
              )}
            </p>
            <p className={styles.viewValue}>{value}</p>
          </div>
        )}

        {/* BUILD/FILL: actual input box */}
        {!isView && (
          <div
            className={cls().inputContainer}
            style={{ width, height }}
            onMouseDown={onMouseDown}
            onDoubleClick={isForm ? onDoubleClick : undefined}
          >
            <input
              type="text"
              placeholder={placeholder}
              value={value || ''}
              required={isFill ? requiredRuntime : false}
              onChange={(e) =>
                isFill &&
                setProp((props: any) => {
                  props.value = e.target.value;
                })
              }
              className={cls().input}
            />

            {/* resize only in FORM */}
            {enabled && isForm && (
              <div
                onMouseDown={onResizeMouseDown}
                className={styles.resizeHandle}
                title="Drag to resize"
              />
            )}
          </div>
        )}

        {/* FILL mode: per-submission required toggle */}
      </div>

      {/* === MODAL EDITOR (CENTERED) === */}
      {showEditor && (
        <BodyPortal>
          <div
            className={styles.modalOverlay}
            role="presentation"
            onMouseDown={() =>
              setShowEditor(false)
            } /* click backdrop to close */
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Edit Input Field"
              onMouseDown={(e) => e.stopPropagation()} /* keep clicks inside */
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit Input</h3>
                <button
                  className={styles.modalClose}
                  aria-label="Close"
                  onClick={() => setShowEditor(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Label</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={label}
                    onChange={(e) =>
                      setProp((props: any) => (props.label = e.target.value))
                    }
                  />
                </div>

                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Placeholder</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={placeholder}
                    onChange={(e) =>
                      setProp(
                        (props: any) => (props.placeholder = e.target.value)
                      )
                    }
                  />
                </div>

                <div
                  className={styles.formRowInline}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    id={`preview-${id}`}
                    type="checkbox"
                    checked={!!previewLabel}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setProp((props: any) => {
                        props.previewLabel = checked;
                      });
                      if (checked) uncheckOtherPreviewLabels(id);
                    }}
                  />
                  <label htmlFor={`preview-${id}`}>Use as Preview Label</label>
                </div>

                {/* Builder-time default required */}
                <div
                  className={styles.formRowInline}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    id={`req-default-${id}`}
                    type="checkbox"
                    checked={!!required}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.required = e.target.checked;
                      })
                    }
                  />
                  <label
                    style={{ marginLeft: 6 }}
                    htmlFor={`req-default-${id}`}
                  >
                    Required field
                  </label>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.btnPrimary}
                  onClick={() => setShowEditor(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

(InputField as any).craft = {
  displayName: 'InputField',
  props: {
    label: 'Label',
    placeholder: 'Type something...',
    value: '',
    left: 0,
    top: 0,
    width: 200,
    height: 40,
    previewLabel: false,
    required: false, // builder-time default
  },
};

export default InputField;
